import type { Breadcrumb, Event } from "@sentry/react";

type SentrySpan = NonNullable<Event["spans"]>[number];

// The router SDK currently emits all three conventions for matched parameters.
const ROUTE_PARAMETER_PREFIXES = ["url.path.params.", "url.path.parameter.", "params."];

function stripUrlDetails(value: string): string {
  // Also handle HTTP span descriptions such as "GET https://user:pass@host/".
  const absolute = /^(.*?\s)?((?:https?:)?\/\/\S+)$/i.exec(value);
  if (absolute?.[2]) {
    try {
      const protocolRelative = absolute[2].startsWith("//");
      const url = new URL(protocolRelative ? `https:${absolute[2]}` : absolute[2]);
      const origin = protocolRelative ? `//${url.host}` : url.origin;
      return `${absolute[1] ?? ""}${origin}${url.pathname}`;
    } catch {
      return "[Invalid URL]";
    }
  }
  return value.split(/[?#]/, 1)[0] ?? "";
}

function sanitizeUrlFields(
  data: Record<string, unknown> | undefined,
  keys: readonly string[],
): void {
  if (!data) return;
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string") data[key] = stripUrlDetails(value);
  }
}

function sanitizeTraceData(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  for (const key of Object.keys(data)) {
    if (ROUTE_PARAMETER_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      delete data[key];
    }
  }
  sanitizeUrlFields(data, ["http.url", "url.full", "http.target"]);
  delete data["url.query"];
  delete data["url.fragment"];
  if (typeof data["url.template"] === "string") {
    data["url.path"] = data["url.template"];
    delete data["url.full"];
  }
}

export function sanitizeSentrySpan(span: SentrySpan): SentrySpan {
  sanitizeTraceData(span.data);
  if (typeof span.data["url.template"] === "string") {
    span.description = span.data["url.template"];
  } else if (span.description) {
    span.description = stripUrlDetails(span.description);
  }
  return span;
}

export function sanitizeSentryEvent<T extends Event>(event: T): T {
  // The browser SDK's HttpContext integration can retain the full page URL
  // even with dataCollection.urlQueryParams=false. Apply this after all
  // integrations, to errors and transactions alike.
  if (event.request?.url) event.request.url = stripUrlDetails(event.request.url);
  sanitizeTraceData(event.contexts?.trace?.data);
  for (const span of event.spans ?? []) sanitizeSentrySpan(span);
  for (const exception of event.exception?.values ?? []) {
    for (const frame of exception.stacktrace?.frames ?? []) {
      if (frame.filename) frame.filename = stripUrlDetails(frame.filename);
      if (frame.abs_path) frame.abs_path = stripUrlDetails(frame.abs_path);
    }
  }
  return event;
}

export function sanitizeSentryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  sanitizeUrlFields(breadcrumb.data, ["url", "from", "to"]);
  return breadcrumb;
}

export function sanitizeSentryReplayEvent(event: Event): Event | null {
  if (event.type !== "replay_event") return event;
  const urls: unknown[] = "urls" in event && Array.isArray(event.urls) ? event.urls : [];
  // rrweb embeds the page URL in its snapshot metadata, outside the SDK's
  // supported recording filter. Discard these segments before transport.
  if ([event.request?.url, ...urls].some((url) => typeof url === "string" && /[?#]/.test(url))) {
    return null;
  }
  if (event.request) {
    delete event.request.headers;
    delete event.request.cookies;
    delete event.request.data;
  }
  delete event.user;
  return event;
}
